/**
 * AcceptSignInPage
 * =================
 *
 * Landing page for sign-in *tokens* (as opposed to invitations) on the
 * unified primary host (`app.tagada.io`). Sister to `accept-invitation`,
 * which handles `signUp.create({ strategy: 'ticket' })` for partner-staff
 * invitation tickets.
 *
 * Why a separate route: the apply flow at `tagadapay.io/apply` creates
 * the merchant's Clerk user *server-side* (passwordless, with apply
 * verdict in publicMetadata) and mints a single-use sign-in token
 * (`clerk.signInTokens.createSignInToken`). The token is consumed by
 * `signIn.create({ strategy: 'ticket' })` — NOT `signUp.create`.
 *
 * If we routed apply tickets to `/accept-invitation`, the call would
 * fail with `form_identifier_exists` because the user already exists,
 * and we'd lose the smooth "submit → already signed in" UX.
 *
 * Why not Clerk's hosted accounts portal at `accounts.tagada.io`: it
 * silently ignores `redirect_url` for sign-in tokens (Clerk falls back
 * to the configured "homepage URL" which is the marketing app), so
 * users land on `www.tagada.io` instead of `dashboard.tagadapay.io`.
 * This page reads `redirect_url`, validates it against our satellite
 * allowlist (`safeSignInRedirectUrl`), and bounces the user across
 * after the session is active — Clerk's cross-domain cookie handshake
 * carries the auth state to the satellite.
 *
 * Security:
 *   - `redirect_url` MUST be on `allowedSignInRedirectOrigins()`
 *     (partner satellites + tagadapay-owned satellites). Any other
 *     value is dropped and we fall through to the local home redirect.
 *   - Sign-in tokens are single-use, server-side rate-limited by
 *     Clerk, and TTL-bound (5 min in our minter).
 *
 * Behaviour:
 *   - Already signed in (re-clicked link, second tab): bounce to
 *     `redirect_url` (or `/`) without trying to consume the ticket.
 *   - No ticket in URL: render an error with a "Sign in normally"
 *     escape hatch.
 *   - Token expired / revoked / malformed: render the same error
 *     screen with the Clerk-provided message.
 *   - `complete` status: setActive → cross-origin redirect.
 */
import { useEffect, useState } from 'react';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@/components/branding/brand-logo';
import { useResolveSignInBrand } from '@/hooks/use-resolve-sign-in-brand';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { safeSignInRedirectUrl } from '@/config/clerk-satellite';

type Phase =
  | { kind: 'loading' }
  | { kind: 'completing' }
  | { kind: 'error'; message: string };

export default function AcceptSignInPage() {
  const { t } = useTranslation('auth');
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get('__clerk_ticket');
  const redirectUrl = safeSignInRedirectUrl(searchParams.get('redirect_url'));
  const brand = useResolveSignInBrand(redirectUrl);
  useBrandTheme(brand);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    if (!authLoaded || !isLoaded) return;

    // Already signed in (re-clicked the magic link, or another tab
    // beat us to it) — go straight to the satellite. No need to
    // re-consume the token; Clerk will reject single-use replays.
    if (isSignedIn) {
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      } else {
        navigate('/');
      }
      return;
    }

    if (!ticket) {
      setPhase({
        kind: 'error',
        message: t('acceptSignIn.errors.missingTicket'),
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setPhase({ kind: 'completing' });
        const result = await signIn.create({
          strategy: 'ticket',
          ticket,
        });

        if (cancelled) return;

        if (result.status === 'complete' && result.createdSessionId) {
          await setActive({ session: result.createdSessionId });
          // Cross-origin redirect via window.location so the satellite
          // sees the new __session_<id> cookie set by Clerk's primary
          // and can establish its own scoped cookie via the standard
          // cross-domain handshake.
          if (redirectUrl) {
            window.location.replace(redirectUrl);
          } else {
            navigate('/');
          }
          return;
        }

        // status !== 'complete' is unexpected for sign-in tickets —
        // they don't require additional factors. Surface a generic
        // error rather than guessing.
        setPhase({
          kind: 'error',
          message: t('acceptSignIn.errors.incomplete'),
        });
      } catch (err: any) {
        if (cancelled) return;
        const code = err?.errors?.[0]?.code as string | undefined;
        const msg = err?.errors?.[0]?.message as string | undefined;
        // Distinguish "expired / revoked" from "malformed" so the
        // user sees something actionable in either case. Both surface
        // through the same error UI to keep the page minimal.
        const friendly =
          code === 'sign_in_token_invalid' || code === 'sign_in_token_expired'
            ? t('acceptSignIn.errors.expired')
            : msg ||
              t('acceptSignIn.errors.generic');
        setPhase({ kind: 'error', message: friendly });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoaded, isLoaded, isSignedIn, ticket, redirectUrl, signIn, setActive, navigate, t]);

  if (phase.kind === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="flex justify-center">
            <BrandLogo brand={brand} />
          </div>
          <h1 className="text-foreground text-2xl font-semibold">
            {t('acceptSignIn.error.title')}
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">{phase.message}</p>
          <a
            href="/sign-in"
            className="text-foreground hover:text-primary inline-block text-sm font-medium underline underline-offset-4"
          >
            {t('acceptSignIn.error.signInManually')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="space-y-4 text-center">
        <div className="border-primary mx-auto h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-muted-foreground text-sm">
          {t('acceptSignIn.completing')}
        </p>
      </div>
    </div>
  );
}
